// src/App.jsx
// ============================================================
// COMPLETE INTEGRATED VERSION WITH 4-PHASE PRO JOURNEY
// UPDATED: EA routes refactored from 4 steps to 3 steps
//   - Step 1: Personalize (/ea/personalization)
//   - Step 2: Evaluate & Shortlist (/ea/opportunities) 
//   - Step 3: Activate (/ea/activate)
// UPDATED: /ea/decisions redirects to /ea/opportunities
// UPDATED: /ea/validation redirects to /ea/activate
// UPDATED: Added /ea/roi-model/:opportunityId route
// UPDATED: Added /ea/use-case/:id/brief and /ea/use-case/:id/rationale routes
// UPDATED: EA Focus Mode for /client-profile and /documents routes
//          When accessed from EA (fromEA=true), renders in AppShell (no sidebar)
// UPDATED: Added /ea/weights route for dedicated weight settings page
// UPDATED: Added /ea/execute/:initiativeId route for Initiative Command Center
// ============================================================

import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { io } from "socket.io-client";

import FinancialQAPage from "./pages/FinancialQAPage";

// Layout
import AppShell from "./layouts/AppShell";
import MainAppLayout from "./layouts/MainAppLayout";
import { ClientProvider } from "./contexts/ClientContext";
import { FilterProvider } from "./contexts/FilterContext";
import { PageContextProvider } from "./contexts/PageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// EA Focus Mode Layout Wrapper
import EAFocusRouteWrapper from "./prototype/layouts/EAFocusRouteWrapper";

// Env (Firebase + Looker Studio)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const lookerStudioEmbedUrl = import.meta.env.VITE_LOOKER_STUDIO_EMBED_URL;

// Pages
import HomePage from "./pages/HomePage";
import MatrixPage from "./pages/MatrixPage.jsx";
import PlaybookPage from "./pages/PlaybookPage";
import StrategicAlignmentDashboard from "./pages/StrategicAlignmentDashboard";
import ClientConfiguratorPage from "./pages/ClientConfiguratorPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import UseCaseResults from "./pages/UseCaseResults";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import LogoutPage from "./pages/LogoutPage";
import UseCaseProfilePage from "./pages/UseCaseProfilePage";
import UseCaseSelector from "./pages/UseCaseSelector";
import UseCaseQADashboard from "./pages/UseCaseQADashboard";
import PlaybookDraftPage from "./pages/PlaybookDraftPage";
import ClientPlaybooksPage from "./pages/ClientPlaybooksPage";
import CapabilitiesSummaryPage from "./pages/CapabilitiesSummaryPage";
import ComprehensiveStrategySummaryPage from "./pages/ComprehensiveStrategySummaryPage";
import StrategySummaryPage from "./pages/StrategySummaryPage";
import DocumentManagementPage from "./pages/DocumentManagementPage";
import ActionBoardPriority from "./pages/ActionBoardPriority";
import SetupPage from "./pages/SetupPage";


// Freemium flow pages
import DataStrategyOverview from "./pages/freemium/DataStrategyOverview";
import UpgradeToProPage from "./pages/freemium/UpgradeToProPage";

// Pro flow pages - 4-PHASE JOURNEY STRUCTURE
import ROIModelingPage from "./pages/pro/ROIModelingPage";
import RoadmapPage from "./pages/pro/RoadmapPage";
import ApprovalPage from "./pages/pro/ApprovalPage";
import ExecutionPage from "./pages/pro/ExecutionPage";
import ResourceCenterPage from "./pages/pro/ResourceCenterPage";

// Shared pages (used by both freemium and pro)
import PersonalizationMasterPage from "./pages/shared/Personalizationmasterpage.jsx";

// Auth pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyInvitationPage from "./pages/VerifyInvitationPage";

// Subscription pages
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionCancelledPage from "./pages/SubscriptionCancelledPage";
import SubscriptionManagementPage from "./pages/SubscriptionManagementPage";

// Auth protection
import PrivateRoute from "./components/PrivateRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";

// Unified survey (primary experience)
import UnifiedSurvey from "./components/UnifiedSurvey";
import UnifiedSurveyModal from "./components/UnifiedSurveyModal";

// Dev/Test survey
import StrategicPrioritiesSurvey from "./components/StrategicPrioritiesSurvey";

// EA Prototype (feature-flagged)
// UPDATED: Added EAWeightsPage for dedicated weight settings
// UPDATED: Added EAInitiativeCommandCenterPage for initiative drill-down
import {
  EAProvider,
  EAPersonalizationPage,
  EAOpportunitiesPage,
  EAUpgradePage,
  EADecisionsPage,                  // Kept for redirect - deprecated route
  EAValidationPage,
  EAActivatePage,            // Now serves /ea/activate (Step 3: Activate)
  EAUseCaseHubPage,                 // Persistent Brief/Scoring/ROI hub
  EAWeightsPage,                    // Dedicated weight settings page
  EAInitiativeCommandCenterPage,    // Initiative Command Center drill-down
} from './prototype';

/** Legacy /ea/roi-model/:id → /ea/use-case/:id/roi (hub tab). */
function RoiModelToHubRedirect() {
  const { opportunityId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/ea/use-case/${opportunityId}/roi`}
      replace
      state={location.state}
    />
  );
}

import {
  EAPersonalizationVariantA,
  EAPersonalizationVariantB,
} from './prototype/variants/personalization';

import "./App.css";

const SOCKET_BASE_URL = import.meta.env.VITE_USE_CASE_SOCKET_URL;
// Socket.IO path; differs from the default when the worker is reached through the load balancer
const SOCKET_PATH = import.meta.env.VITE_USE_CASE_SOCKET_PATH || "/socket.io";

/**
 * Route wrapper to render the UnifiedSurvey as a full page.
 */
function UnifiedSurveyRoute() {
  const { type } = useParams();
  const { session, sessionLoading } = useAuth();

  const clientId = session?.client_id || null;
  const userId = session?.user_id || "admin";

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6">
          <div className="text-amber-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
          <p className="text-slate-600 mb-4">
            Please ensure you're logged in with an organization to access surveys.
          </p>
          <button
            onClick={() => window.location.assign('/home')}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const getSurveyTitle = (surveyType) => {
    switch (surveyType) {
      case "strategy":
        return "Strategic Priorities Assessment";
      case "capabilities":
        return "Data & Analytics Capabilities";
      case "readiness":
        return "Data Readiness and Governance";
      default:
        return `${surveyType.charAt(0).toUpperCase() + surveyType.slice(1)} Survey`;
    }
  };

  return (
    <UnifiedSurvey
      isOpen={true}
      surveyType={type}
      surveyTitle={getSurveyTitle(type)}
      clientId={clientId}
      userId={userId}
      userProfile={{ client_id: clientId, user_id: userId }}
      onComplete={({ clientId: cid, surveyType: t }) => {
        window.location.assign(`/summary/${t}?client_id=${cid}`);
      }}
      onSaveAndExit={() => {
        window.history.back();
      }}
      allowEdit={true}
      showReview={true}
      enableDraftRecovery={true}
      enableKeyboardNavigation={true}
    />
  );
}

/**
 * Optional modal route: /survey/:type/modal
 */
function UnifiedSurveyModalRoute() {
  const { type } = useParams();
  const { session, sessionLoading } = useAuth();

  const clientId = session?.client_id || null;
  const userId = session?.user_id || "admin";

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6">
          <div className="text-amber-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
          <p className="text-slate-600 mb-4">
            Please ensure you're logged in with an organization to access surveys.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <UnifiedSurveyModal
      isOpen={true}
      surveyType={type}
      clientId={clientId}
      userId={userId}
      userProfile={{ client_id: clientId, user_id: userId }}
      onClose={() => {
        window.history.back();
      }}
      onComplete={({ clientId: cid, surveyType: t }) => {
        window.location.assign(`/summary/${t}?client_id=${cid}`);
      }}
      onSaveAndExit={() => {
        window.history.back();
      }}
    />
  );
}

/**
 * Wrapper for ClientProfilePage to extract clientId from params
 */
function ClientProfileRoute() {
  const { clientId } = useParams();
  return <ClientProfilePage clientId={String(clientId)} />;
}

/**
 * Wrapper for ClientProfilePage using session.client_id from AuthContext
 */
function ClientProfileAuthRoute() {
  const { session } = useAuth();
  return <ClientProfilePage clientId={session?.client_id} />;
}

/**
 * Wrapper for DocumentManagementPage using session.client_id from AuthContext
 */
function DocumentManagementAuthRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <DocumentManagementPage clientId={session?.client_id} />;
}

/**
 * Wrapper for DocumentManagementPage to extract clientId from params
 */
function DocumentManagementRoute() {
  const { clientId } = useParams();
  return <DocumentManagementPage clientId={String(clientId)} />;
}

/**
 * Wrapper for StrategicAlignmentDashboard using session.client_id from AuthContext
 */
function StrategicAlignmentAuthRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <StrategicAlignmentDashboard clientId={session?.client_id} useCaseId={10} />;
}

/**
 * Wrapper for ActionBoardPriority to extract clientId from params
 */
function ActionBoardRoute() {
  const { clientId } = useParams();
  return <ActionBoardPriority />;
}

/**
 * Wrapper for test survey using session.client_id from AuthContext
 */
function TestSurveyRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const clientId = session?.client_id || "101";
  const userId = session?.user_id || "admin";

  return (
    <StrategicPrioritiesSurvey
      userProfile={{ client_id: clientId, user_id: userId }}
      onComplete={() => console.log("Test survey completed")}
      onSaveAndExit={() => console.log("Test survey saved and exited")}
      onGoToSummary={() => console.log("Go to summary")}
      initialResponses={{}}
      initialCompletionStatus={false}
    />
  );
}

function GlobalSocketManager() {
  const { session, sessionLoading } = useAuth();
  const socketRef = useRef(null);

  useEffect(() => {
    if (sessionLoading) return;

    const currentClientId = session?.client_id;

    if (!currentClientId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    if (!SOCKET_BASE_URL) return;
    const socket = io(SOCKET_BASE_URL, {
      path: SOCKET_PATH,
      transports: ["polling", "websocket"],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[socket] connected", {
        socketId: socket.id,
        clientId: currentClientId,
        url: SOCKET_BASE_URL,
      });
      socket.emit("document:subscribe", { clientId: currentClientId });
    });

    socket.on("disconnect", (reason) => {
      console.log("[socket] disconnected", {
        reason,
        clientId: currentClientId,
      });
    });

    socket.on("connect_error", (error) => {
      console.warn("[socket] connection error", {
        message: error?.message || String(error),
        clientId: currentClientId,
        url: SOCKET_BASE_URL,
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionLoading, session?.client_id]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <ClientProvider>
        <FilterProvider>
          <PageContextProvider>
            <Router>
              <GlobalSocketManager />
              <Routes>
                {/* Auth routes (no layout) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-invitation" element={<VerifyInvitationPage />} />

                {/* Subscription flow pages (public) */}
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/subscription/cancelled" element={<SubscriptionCancelledPage />} />

                {/* Full-screen (no header) - Protected */}
                <Route path="/survey/:type" element={<PrivateRoute><UnifiedSurveyRoute /></PrivateRoute>} />
                <Route path="/survey/:type/modal" element={<PrivateRoute><UnifiedSurveyModalRoute /></PrivateRoute>} />
                <Route path="/setup" element={<PrivateRoute><SetupPage /></PrivateRoute>} />
                <Route path="/setup/:clientId" element={<PrivateRoute><SetupPage /></PrivateRoute>} />

                {/* UNIFIED HOME ROUTE */}
                <Route
                  path="/home"
                  element={
                    <PrivateRoute>
                      <AppShell>
                        <HomePage />
                      </AppShell>
                    </PrivateRoute>
                  }
                />

                {/* SUBSCRIPTION MANAGEMENT */}
                <Route
                  path="/subscription"
                  element={
                    <PrivateRoute>
                      <RoleProtectedRoute allowedRoles={["CLIENT_ADMIN"]}>
                        <AppShell>
                          <SubscriptionManagementPage />
                        </AppShell>
                      </RoleProtectedRoute>
                    </PrivateRoute>
                  }
                />

                {/* SHARED PERSONALIZE ROUTE */}
                <Route
                  path="/personalize"
                  element={
                    <PrivateRoute>
                      <AppShell>
                        <PersonalizationMasterPage />
                      </AppShell>
                    </PrivateRoute>
                  }
                />

                {/* Freemium routes */}
                <Route
                  path="/freemium/*"
                  element={
                    <PrivateRoute>
                      <AppShell>
                        <Routes>
                          <Route path="/" element={<Navigate to="/home" replace />} />
                          <Route path="home" element={<Navigate to="/home" replace />} />
                          <Route path="snapshot" element={<Navigate to="/home" replace />} />
                          <Route path="personalize" element={<Navigate to="/personalize" replace />} />
                          <Route path="strategy" element={<DataStrategyOverview />} />
                          <Route path="upgrade" element={<UpgradeToProPage />} />
                        </Routes>
                      </AppShell>
                    </PrivateRoute>
                  }
                />

                {/* ============================================================ */}
                {/* EA PROTOTYPE ROUTES - 3-STEP JOURNEY (Refactored Jan 2026)   */}
                {/*                                                              */}
                {/* Step 1: Personalize    → /ea/personalization                 */}
                {/* Step 2: Evaluate & Shortlist → /ea/opportunities             */}
                {/* Step 3: Activate       → /ea/activate                        */}
                {/*                                                              */}
                {/* WEIGHT SETTINGS:                                             */}
                {/*   /ea/weights → Dedicated weight configuration page          */}
                {/*                                                              */}
                {/* USE CASE DETAIL ROUTES:                                      */}
                {/*   /ea/use-case/:id/brief → Profile page                      */}
                {/*   /ea/use-case/:id/rationale → Scoring rationale page        */}
                {/*   /ea/roi-model/:opportunityId → ROI Model drilldown         */}
                {/*                                                              */}
                {/* INITIATIVE COMMAND CENTER:                                   */}
                {/*   /ea/execute/:initiativeId → Command Center drill-down      */}
                {/*                                                              */}
                {/* DEPRECATED ROUTES (redirects in place):                      */}
                {/*   /ea/decisions → /ea/opportunities (Prioritize merged)      */}
                {/*   /ea/validation → /ea/activate (renamed)                    */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/ea/*"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <AppShell hideSecondaryNav={true}>
                            <Routes>
                              {/* Default route goes to personalization */}
                              <Route path="/" element={<Navigate to="/ea/personalization" replace />} />

                              {/* ========================================== */}
                              {/* STEP 1: PERSONALIZE                        */}
                              {/* ========================================== */}
                              <Route path="personalization" element={<EAPersonalizationPage />} />

                              {/* Temporary variant routes (layout explorations) */}
                              <Route path="personalization-variant-a" element={<EAPersonalizationVariantA />} />
                              <Route path="personalization-variant-b" element={<EAPersonalizationVariantB />} />

                              {/* Legacy redirect: /ea/context → /ea/personalization */}
                              <Route path="context" element={<Navigate to="/ea/personalization" replace />} />

                              {/* ========================================== */}
                              {/* WEIGHT SETTINGS PAGE                       */}
                              {/* Accessible from Personalization and        */}
                              {/* Opportunities pages via "Adjust Weights"   */}
                              {/* ========================================== */}
                              <Route path="weights" element={<EAWeightsPage />} />

                              {/* ========================================== */}
                              {/* STEP 2: EVALUATE & SHORTLIST               */}
                              {/* (Merged "Prioritize" functionality)        */}
                              {/* ========================================== */}
                              <Route path="opportunities" element={<EAOpportunitiesPage />} />

                              {/* DEPRECATED: Old Prioritize route redirects to Evaluate */}
                              <Route path="decisions" element={<Navigate to="/ea/opportunities" replace />} />

                              {/* ========================================== */}
                              {/* STEP 3: ACTIVATE                           */}
                              {/* (Formerly "Validation")                    */}
                              {/* ========================================== */}
                              <Route path="activate" element={<EAActivatePage />} />

                              {/* DEPRECATED: Old Validation route redirects to Activate */}
                              <Route path="validation" element={<Navigate to="/ea/activate" replace />} />

                              {/* ========================================== */}
                              {/* USE-CASE HUB (Brief · Scoring · ROI)       */}
                              {/* Single parent keeps chrome mounted.        */}
                              {/* Legacy /ea/roi-model/:id redirects here.   */}
                              {/* ========================================== */}
                              <Route path="use-case/:id/:tab" element={<EAUseCaseHubPage />} />
                              <Route
                                path="roi-model/:opportunityId"
                                element={<RoiModelToHubRedirect />}
                              />

                              {/* ========================================== */}
                              {/* INITIATIVE COMMAND CENTER                  */}
                              {/* Drill-down from Execute portfolio           */}
                              {/* Click initiative card → command center      */}
                              {/* ========================================== */}
                              <Route path="execute/:initiativeId" element={<EAInitiativeCommandCenterPage />} />

                              {/* ========================================== */}
                              {/* UPGRADE PAGE                               */}
                              {/* ========================================== */}
                              <Route path="upgrade" element={<EAUpgradePage />} />
                            </Routes>
                          </AppShell>
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* Pro routes - 4-PHASE JOURNEY STRUCTURE */}
                <Route
                  path="/pro/*"
                  element={
                    <PrivateRoute>
                      <AppShell>
                        <Routes>
                          <Route path="/" element={<Navigate to="/home" replace />} />
                          <Route path="home" element={<Navigate to="/home" replace />} />
                          <Route path="personalize" element={<Navigate to="/personalize" replace />} />

                          {/* PHASE 2: ANALYSIS & QUALIFICATION */}
                          <Route path="analysis" element={<ROIModelingPage />} />
                          <Route path="analysis/:useCaseId" element={<ROIModelingPage />} />

                          {/* PHASE 3: ROADMAP PLANNING */}
                          <Route path="roadmap-planning" element={<RoadmapPage />} />
                          <Route path="roadmap-planning/:useCaseId" element={<RoadmapPage />} />

                          {/* PHASE 4: APPROVAL → EXECUTION */}
                          <Route path="approval" element={<ApprovalPage />} />
                          <Route path="execution" element={<ExecutionPage />} />

                          {/* LEGACY REDIRECTS */}
                          <Route path="roi" element={<Navigate to="/pro/analysis" replace />} />
                          <Route path="roi/:useCaseId" element={<Navigate to="/pro/analysis/:useCaseId" replace />} />
                          <Route path="roadmap" element={<Navigate to="/pro/roadmap-planning" replace />} />
                          <Route path="roadmap/:useCaseId" element={<Navigate to="/pro/roadmap-planning/:useCaseId" replace />} />
                        </Routes>
                      </AppShell>
                    </PrivateRoute>
                  }
                />

                {/* ============================================================ */}
                {/* EA FOCUS MODE ROUTES                                         */}
                {/* These routes support EA Focus Mode:                          */}
                {/* - When accessed from EA (fromEA=true): AppShell (no sidebar) */}
                {/* - When accessed normally: MainAppLayout (with sidebar)       */}
                {/* ============================================================ */}

                {/* Client Profile - EA Focus Mode enabled */}
                <Route
                  path="/client-profile"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <ClientProfileAuthRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/client-profile/:clientId"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <ClientProfileRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />

                {/* Document Management - EA Focus Mode enabled */}
                <Route
                  path="/documents"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <DocumentManagementAuthRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/documents/:clientId"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <DocumentManagementRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />

                {/* Main app routes use MainAppLayout (sidebar + header) */}
                <Route
                  path="*"
                  element={
                    <PrivateRoute>
                      <MainAppLayout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/ea/personalization" replace />} />
                          <Route path="home/:clientId" element={<HomePage />} />

                          {/* Client Configurator / Data Input */}
                          <Route path="client-configurator" element={<ClientConfiguratorPage />} />
                          <Route path="client-configurator/:clientId" element={<ClientConfiguratorPage />} />
                          <Route path="client-data-input/:clientId" element={<ClientConfiguratorPage />} />

                          {/* NOTE: /documents and /client-profile routes moved above for EA Focus Mode */}

                          {/* Action Board */}
                          <Route path="action-board" element={<ActionBoardPriority />} />
                          <Route path="action-board/:clientId" element={<ActionBoardRoute />} />

                          {/* Matrix */}
                          <Route
                            path="matrix"
                            element={
                              <MatrixPage
                                __firebase_config={JSON.stringify(firebaseConfig)}
                                __looker_studio_embed_url={lookerStudioEmbedUrl}
                                __initial_auth_token={null}
                                __app_id="dva-frontend-vite"
                              />
                            }
                          />

                          {/* Use Case Results */}
                          <Route path="results" element={<UseCaseResults />} />

                          {/* Playbooks */}
                          <Route path="playbook" element={<PlaybookPage />} />
                          <Route path="playbook-draft/:id" element={<PlaybookDraftPage />} />

                          {/* Strategy & Summaries */}
                          <Route path="strategic-alignment" element={<StrategicAlignmentAuthRoute />} />
                          <Route path="capabilities-summary" element={<CapabilitiesSummaryPage />} />
                          <Route path="strategy-summary" element={<ComprehensiveStrategySummaryPage />} />
                          <Route path="summary/:type" element={<StrategySummaryPage />} />

                          {/* Use Case Profile */}
                          <Route path="usecase/:id" element={<UseCaseProfilePage />} />

                          {/* QA */}
                          <Route path="qa" element={<Navigate to="/qa/selector" replace />} />
                          <Route path="qa/selector" element={<UseCaseSelector />} />
                          <Route path="qa/:useCaseId" element={<UseCaseQADashboard />} />

                          {/* Financial QA */}
                          <Route path="financial-qa" element={<FinancialQAPage />} />
                          <Route path="financial-qa/:clientId" element={<FinancialQAPage />} />

                          {/* Utilities */}
                          <Route path="chat" element={<ChatPage />} />
                          <Route path="settings" element={<SettingsPage />} />
                          <Route path="logout" element={<LogoutPage />} />

                          {/* Dev/Test */}
                          <Route path="test-survey" element={<TestSurveyRoute />} />

                          {/* 404 → EA Personalization */}
                          <Route path="*" element={<Navigate to="/ea/personalization" replace />} />
                        </Routes>
                      </MainAppLayout>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Router>
          </PageContextProvider>
        </FilterProvider>
      </ClientProvider>
    </AuthProvider>
  );
}